import { queryOptions } from '@tanstack/react-query'
import { setCsrfToken, unwrap } from './client'
import type { LivenessOut, MeOut, SearchHit, SessionStatus } from './generated'
import { livenessApiV1LivenessGet, searchApiV1SearchGet, sessionStatusApiV1AuthSessionGet } from './generated'

// ---------------------------------------------------------------------------------------------------
// Live API (web-shell): session, liveness, search. Generated SDK + unwrap(); errors are thrown, never
// turned into empty answers (P11).
// ---------------------------------------------------------------------------------------------------

export type Me = MeOut

/** `GET /api/v1/auth/session` never 401s; it says whether this browser is signed in. */
export const sessionQuery = () =>
  queryOptions({
    queryKey: ['session'],
    queryFn: async (): Promise<SessionStatus> => {
      const s = await unwrap(sessionStatusApiV1AuthSessionGet())
      if (!s || typeof s.signed_in !== 'boolean') throw new Error('session answer malformed')
      setCsrfToken(s.me?.csrf_token ?? null)
      return s
    },
    staleTime: 5 * 60_000,
    retry: false,
  })

export interface LivenessView {
  /** Age of the last Desk event; null = never seen or unknown. */
  deskEventAgeMinutes: number | null
  /** When the last Desk event landed (ISO); null = never seen or unknown. */
  lastDeskEventAt: string | null
  mirrorState: LivenessOut['mirror_state']
  sendsDisabled: boolean
  sendsDisabledReason: string | null
  /** Signed-in staff only. Why Riley stopped, when an incident is open (#1048). */
  rileyBanner?: string | null
}

export function toLivenessView(l: LivenessOut): LivenessView {
  const secs = l.last_desk_event_age_seconds
  return {
    deskEventAgeMinutes: typeof secs === 'number' ? secs / 60 : null,
    lastDeskEventAt: l.last_desk_event_at ?? null,
    mirrorState: l.mirror_state,
    sendsDisabled: l.sends_disabled,
    sendsDisabledReason: l.sends_disabled_reason ?? null,
    rileyBanner: l.riley_banner ?? null,
  }
}

/** Desk-event liveness (P12). Polled every minute so the pill ages even when no event arrives; SSE also invalidates it. */
export const livenessQuery = () =>
  queryOptions({
    queryKey: ['liveness'],
    queryFn: async () => toLivenessView(await unwrap(livenessApiV1LivenessGet())),
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: false,
  })

/** Palette search (`GET /api/v1/search`, slice api-cases). */
export const searchQuery = (q: string) =>
  queryOptions({
    queryKey: ['search', q],
    queryFn: async (): Promise<SearchHit[]> => {
      const hits = await unwrap(searchApiV1SearchGet({ query: { q } }))
      if (!Array.isArray(hits)) throw new Error('search answer malformed')
      return hits
    },
    enabled: q.trim().length >= 2,
    staleTime: 30_000,
    retry: false,
  })
